import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {apiErrorMessage, getPortfolioBoard} from './api/abfApi';
import {overviewApi, type ProgramCard} from './forwardFlow/forwardFlowOverviewApi';
import type {PortfolioBoard} from './types/board';
import {money} from './board/boardShared';
import {Button, INK} from './onboarding/review/reviewStyles';
import {Surface} from './onboarding/review/contractReviewStyles';
import PortfolioBoardPage from './board/PortfolioBoardPage';
import ABFManagementScreen from '../../components/credit/ABFManagementScreen';
import {ForwardFlowDirectoryPage} from './forwardFlow/ForwardFlowPages';
import {useViewer} from './forwardFlow/ForwardFlowShared';
import NewFacilityLanes from './onboarding/NewFacilityLanes';
import type {AbfHomeView} from './routes';

/* Facilities read blue, forward-flow reads amber — the family is legible before a number. */
const FAC = '#5aa9f0';
const FF = '#e6b24a';

const Hero = styled.div`
  margin: 2px 0 4px;
  .eyebrow { text-transform: uppercase; letter-spacing: .14em; font-size: 11px; font-weight: 800; color: ${INK.accent}; }
  h1 { margin: 6px 0 6px; color: ${INK.strong}; font-size: clamp(20px, 2.4vw, 26px); line-height: 1.15; }
  p { margin: 0; color: ${INK.muted}; max-width: 70ch; font-size: 14px; }
  p b.fac { color: ${FAC}; } p b.ff { color: ${FF}; }
`;

const Seg = styled.div`
  display: inline-flex; gap: 4px; margin: 18px 0 16px; padding: 4px;
  border: 1px solid ${INK.lineStrong}; border-radius: 12px; background: ${INK.panel};
  button {
    appearance: none; border: 0; background: transparent; color: ${INK.muted}; font: inherit; font-weight: 700;
    padding: 9px 16px; border-radius: 9px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
    transition: background .15s, color .15s;
  }
  button .dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; opacity: .55; }
  button:hover { color: ${INK.body}; }
  button[aria-selected='true'] { background: ${INK.well}; color: ${INK.strong}; box-shadow: inset 0 0 0 1px ${INK.lineStrong}; }
  button[data-fam='fac'][aria-selected='true'] { color: ${FAC}; }
  button[data-fam='ff'][aria-selected='true'] { color: ${FF}; }
  button:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 2px; }
`;

/* The two ways to look at facilities: the simple list (default) and the monitoring board. */
const SubSeg = styled.div`
  display: inline-flex; gap: 2px; margin: 0 0 14px; padding: 3px;
  border: 1px solid ${INK.line}; border-radius: 9px; background: ${INK.panel};
  button {
    appearance: none; border: 0; background: transparent; color: ${INK.muted}; font: inherit; font-size: 12.5px;
    font-weight: 700; padding: 6px 12px; border-radius: 7px; cursor: pointer;
  }
  button:hover { color: ${INK.body}; }
  button[aria-selected='true'] { background: ${INK.well}; color: ${FAC}; box-shadow: inset 0 0 0 1px ${INK.lineStrong}; }
  button:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 2px; }
`;

const TopRow = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px;
`;

const Two = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const ProdCard = styled.button<{$fam: 'fac' | 'ff'}>`
  text-align: left; appearance: none; cursor: pointer; width: 100%;
  display: flex; flex-direction: column; overflow: hidden;
  border: 1px solid ${INK.line}; border-radius: 14px; background: ${INK.panel}; color: ${INK.body};
  box-shadow: inset 0 3px ${p => (p.$fam === 'fac' ? FAC : FF)};
  transition: border-color .15s, background .15s;
  &:hover, &:focus-visible { border-color: ${p => (p.$fam === 'fac' ? FAC : FF)}; background: ${INK.raised}; outline: none; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding: 16px 18px; border-bottom: 1px solid ${INK.line}; }
  .top h2 { margin: 0; font-size: 15px; color: ${INK.strong}; }
  .top p { margin: 3px 0 0; font-size: 12px; color: ${INK.muted}; }
  .chip { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; padding: 4px 9px; border-radius: 999px; white-space: nowrap;
    color: ${p => (p.$fam === 'fac' ? FAC : FF)}; background: ${p => (p.$fam === 'fac' ? 'rgba(90,169,240,.12)' : 'rgba(230,178,74,.12)')};
    box-shadow: inset 0 0 0 1px ${p => (p.$fam === 'fac' ? 'rgba(90,169,240,.4)' : 'rgba(230,178,74,.4)')}; }
  .k { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: ${INK.line}; }
  .k .c { background: ${INK.raised}; padding: 12px 15px; min-width: 0; }
  .k .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: .07em; color: ${INK.faint}; font-weight: 800; }
  .k .val { font-size: 18px; margin-top: 4px; color: ${INK.strong}; font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .foot { margin-top: auto; padding: 12px 18px; border-top: 1px solid ${INK.line}; }
  .foot span { font-size: 12.5px; font-weight: 700; color: ${INK.accent}; }
`;

const Note = styled.p`
  margin: 4px 0 8px; padding: 12px 15px; border: 1px solid ${INK.line}; border-left: 3px solid ${INK.accent};
  border-radius: 8px; background: ${INK.panel}; color: ${INK.muted}; font-size: 12.5px;
  b { color: ${INK.body}; }
`;

const SEGMENTS: Array<{view: AbfHomeView; label: string; fam?: 'fac' | 'ff'}> = [
  {view: 'overview', label: 'Overview'},
  {view: 'facilities', label: 'Facilities', fam: 'fac'},
  {view: 'forward-flow', label: 'Forward-Flow Programs', fam: 'ff'},
];

const isView = (v: string | null): v is AbfHomeView =>
  v === 'overview' || v === 'facilities' || v === 'forward-flow';

/**
 * The one ABF home. Asset-based finance covers two structurally different products —
 * a facility lends against a pledged pool; a forward-flow program buys the loans
 * outright — so each keeps its own summary and they never share a register. A
 * segmented control moves between the overview and the two product surfaces.
 */
export const AbfHome: React.FC<{embedded?: boolean}> = ({embedded}) => {
  const viewer = useViewer();
  const [choosing, setChoosing] = useState(false);
  const [params, setParams] = useSearchParams();
  const view: AbfHomeView = isView(params.get('view')) ? (params.get('view') as AbfHomeView) : 'overview';
  const setView = (next: AbfHomeView) =>
    setParams(prev => {
      const p = new URLSearchParams(prev);
      if (next === 'overview') p.delete('view'); else p.set('view', next);
      if (next !== 'facilities') p.delete('mode');
      return p;
    });

  // Facilities opens on the simple list; ?mode=board switches to the monitoring board.
  const facilitiesMode: 'list' | 'board' = params.get('mode') === 'board' ? 'board' : 'list';
  const setFacilitiesMode = (next: 'list' | 'board') =>
    setParams(prev => {
      const p = new URLSearchParams(prev);
      if (next === 'board') p.set('mode', 'board'); else p.delete('mode');
      return p;
    });

  const [board, setBoard] = useState<PortfolioBoard | null>(null);
  const [programs, setPrograms] = useState<ProgramCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([getPortfolioBoard(false, false), overviewApi.directory()]);
      setBoard(b); setPrograms(p); setError(null);
    } catch (e) { setError(apiErrorMessage(e, 'The ABF summary could not be loaded.')); }
  }, []);
  // Only the overview needs the aggregate; the segment pages fetch their own data.
  useEffect(() => { if (view === 'overview') void loadOverview(); }, [view, loadOverview]);

  const ff = useMemo(() => {
    const list = programs ?? [];
    const sum = (pick: (c: ProgramCard) => number | null | undefined) =>
      list.reduce((acc, c) => acc + (Number(pick(c)) || 0), 0);
    const open = list.filter(c => (c.status || '').toUpperCase().includes('OPEN')
      || (c.statusLabel || '').toLowerCase().includes('open')).length;
    return {
      programs: list.length, open,
      loans: sum(c => c.facts?.owned.loans),
      upb: sum(c => c.facts?.owned.upb),
      remaining: sum(c => (c.facts?.capacity.known ? c.facts?.capacity.remaining : 0)),
      owed: sum(c => c.facts?.owedToSeller.unpaid),
    };
  }, [programs]);

  const t = board?.totals;

  const body = <>
    <TopRow>
      <Seg role="tablist" aria-label="ABF surface">
        {SEGMENTS.map(s => (
          <button key={s.view} role="tab" aria-selected={view === s.view} data-fam={s.fam}
            onClick={() => setView(s.view)}>
            {s.fam && <span className="dot" />}{s.label}
          </button>
        ))}
      </Seg>
      {viewer.maker && view !== 'forward-flow' &&
        <Button $variant="primary" type="button" onClick={() => setChoosing(c => !c)}>+ Onboard a facility</Button>}
    </TopRow>

    {choosing && <div style={{margin: '4px 0 16px'}}><NewFacilityLanes onClose={() => setChoosing(false)} /></div>}

    {view === 'overview' && <>
      <Hero>
        <div className="eyebrow">Credit &amp; Loans · Asset-Based Finance</div>
        <h1>One ABF home, two operating surfaces</h1>
        <p>Asset-based finance covers two structurally different products. A <b className="fac">facility</b> lends
          against a pledged pool; a <b className="ff">forward-flow program</b> buys the loans outright. They share
          this home but never share a register — each keeps its own summary.</p>
      </Hero>

      {error && <Note role="alert"><b>Could not load the summary.</b> {error}</Note>}

      <Two>
        <ProdCard $fam="fac" type="button" onClick={() => setView('facilities')}
          aria-label="Open Facilities">
          <div className="top">
            <div><h2>Borrowing-Base Facilities</h2>
              <p>Warehouse revolvers — you hold a facility position, loans are collateral.</p></div>
            <span className="chip">{t ? `${t.facilities} on board` : '—'}</span>
          </div>
          <div className="k">
            <div className="c"><div className="lbl">Commitment</div><div className="val">{t ? money(t.commitment) : '—'}</div></div>
            <div className="c"><div className="lbl">Exposure</div><div className="val">{t ? money(t.exposure) : '—'}</div></div>
            <div className="c"><div className="lbl">Borrowing base</div><div className="val">{t ? money(t.borrowingBase) : '—'}</div></div>
            <div className="c"><div className="lbl">Availability</div><div className="val">{t ? money(t.availability) : '—'}</div></div>
          </div>
          <div className="foot"><span>Open Facilities →</span></div>
        </ProdCard>

        <ProdCard $fam="ff" type="button" onClick={() => setView('forward-flow')}
          aria-label="Open Forward-Flow Programs">
          <div className="top">
            <div><h2>Forward-Flow Programs</h2>
              <p>Recurring purchase programs — you own each loan on the official book.</p></div>
            <span className="chip">{programs ? `${ff.programs} program${ff.programs === 1 ? '' : 's'}` : '—'}</span>
          </div>
          <div className="k">
            <div className="c"><div className="lbl">Loans owned</div><div className="val">{programs ? ff.loans.toLocaleString('en-US') : '—'}</div></div>
            <div className="c"><div className="lbl">Owned balance</div><div className="val">{programs ? money(ff.upb) : '—'}</div></div>
            <div className="c"><div className="lbl">Limit remaining</div><div className="val">{programs ? money(ff.remaining) : '—'}</div></div>
            <div className="c"><div className="lbl">Owed to sellers</div><div className="val">{programs ? money(ff.owed) : '—'}</div></div>
          </div>
          <div className="foot"><span>Open Forward-Flow →</span></div>
        </ProdCard>
      </Two>

      <Note><b>Why two surfaces, one home.</b> The facility summary and the forward-flow summary are computed and
        shown independently, so an empty facility book never hides a funded forward-flow book (and the other way
        round). Risk still aggregates them under one Asset-Based Finance node in the IBOR register.</Note>
    </>}

    {view === 'facilities' && <>
      <SubSeg role="tablist" aria-label="Facilities view">
        <button role="tab" type="button" aria-selected={facilitiesMode === 'list'}
          onClick={() => setFacilitiesMode('list')}>Facility list</button>
        <button role="tab" type="button" aria-selected={facilitiesMode === 'board'}
          onClick={() => setFacilitiesMode('board')}>Monitoring board</button>
      </SubSeg>
      {facilitiesMode === 'list' ? <ABFManagementScreen embedded /> : <PortfolioBoardPage embedded />}
    </>}
    {view === 'forward-flow' && <ForwardFlowDirectoryPage embedded />}
  </>;
  // Standalone route gets the page Surface; embedded in the Credit & Loans hub it
  // renders bare so the hub's own content panel provides the chrome.
  return embedded ? <div>{body}</div> : <Surface>{body}</Surface>;
};

export default AbfHome;
